import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/auth';
import { useMessaging } from '../contexts/MessagingContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { api } from '../lib/api';

// ─── KinCare Logo ─────────────────────────────────────────────────────────────

function KinCareLogo({ variant = 'dark' }: { variant?: 'dark' | 'light' }) {
  // Circles are lighter on dark backgrounds, darker on light — matching branding spec
  const c1 = variant === 'dark' ? '#7aadc8' : '#4a7fa5';
  const c2 = variant === 'dark' ? '#9ac4b2' : '#6a9e8a';
  const gradId = variant === 'dark' ? 'kc-grad-dark' : 'kc-grad-light';
  return (
    <div className="flex items-center gap-2.5">
      <svg width="26" height="26" viewBox="0 0 100 100" fill="none" aria-hidden="true">
        <circle cx="38" cy="50" r="22" stroke={c1} strokeWidth="9" />
        <circle cx="62" cy="50" r="22" stroke={c2} strokeWidth="9" />
        <path d="M50 31.5 A22 22 0 0 1 50 68.5" stroke={`url(#${gradId})`} strokeWidth="9" />
        <defs>
          <linearGradient id={gradId} x1="50" y1="28" x2="50" y2="72" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={c1} />
            <stop offset="100%" stopColor={c2} />
          </linearGradient>
        </defs>
      </svg>
      {/* "Kin" in brand blue; "Care" inherits base — white on dark, charcoal on light */}
      <span className={`font-fraunces text-[17px] font-light tracking-tight leading-none ${variant === 'dark' ? 'text-white' : 'text-[#243040]'}`}>
        <em
          className={`font-medium not-italic ${variant === 'dark' ? 'text-primary-300' : 'text-primary-500'}`}
          style={{ fontStyle: 'italic' }}
        >Kin</em>Care
      </span>
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  );
}

function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  );
}

function BookOpenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function GearIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

// ─── Nav items ────────────────────────────────────────────────────────────────

type NavItem = { to: string; label: string; Icon: (props: { className?: string }) => React.ReactElement };

// ─── AppLayout ────────────────────────────────────────────────────────────────

export function AppLayout() {
  const { user } = useAuth();
  const { totalUnreadCount } = useMessaging();
  const { workspaces, activeWorkspace, loading: workspaceLoading } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();

  const [notificationCount, setNotificationCount] = useState(0);

  // Build nav items inside component so we can react to workspaces count
  const isMultiCircle = workspaces.length > 1;
  const SIDEBAR_NAV_ITEMS: NavItem[] = [
    ...(isMultiCircle ? [{ to: '/home', label: 'Home', Icon: HomeIcon }] : []),
    { to: '/dashboard', label: 'Today', Icon: CalendarIcon },
    { to: '/calendar', label: 'Calendar', Icon: CalendarIcon },
    { to: '/people', label: 'People', Icon: UsersIcon },
    { to: '/messages', label: 'Messages', Icon: ChatIcon },
    { to: '/journal', label: 'Journal', Icon: BookOpenIcon },
    { to: '/checklists', label: 'Checklists', Icon: ClipboardIcon },
    { to: '/notifications', label: 'Notifications', Icon: BellIcon },
  ];

  const MOBILE_TAB_ITEMS: NavItem[] = [
    ...(isMultiCircle ? [{ to: '/home', label: 'Home', Icon: HomeIcon }] : []),
    { to: '/dashboard', label: 'Today', Icon: isMultiCircle ? CalendarIcon : HomeIcon },
    { to: '/calendar', label: 'Cal', Icon: CalendarIcon },
    { to: '/people', label: 'People', Icon: UsersIcon },
    { to: '/journal', label: 'Journal', Icon: BookOpenIcon },
    { to: '/messages', label: 'Msgs', Icon: ChatIcon },
  ];

  // Redirect: no workspaces → onboarding; root path → /home (multi) or /dashboard (single)
  useEffect(() => {
    if (workspaceLoading) return;
    if (workspaces.length === 0) {
      navigate('/onboarding', { replace: true });
    } else if (location.pathname === '/') {
      navigate(isMultiCircle ? '/home' : '/dashboard', { replace: true });
    }
  }, [workspaceLoading, workspaces.length, isMultiCircle, location.pathname, navigate]);

  const familyId = activeWorkspace?.type === 'family' ? activeWorkspace.id : null;

  // Poll pending approval count so the badge stays current
  useEffect(() => {
    if (!familyId) return;
    const fetch = () => {
      api.getPendingApprovals(familyId)
        .then((data) => setNotificationCount(data.length))
        .catch(() => {});
    };
    fetch();
    const interval = setInterval(fetch, 30000);
    return () => clearInterval(interval);
  }, [familyId]);

  // Set CSS variable for mobile top bar height
  useEffect(() => {
    const update = () => {
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      document.documentElement.style.setProperty('--app-bar-height', isMobile ? '48px' : '0px');
    };
    update();
    const mq = window.matchMedia('(max-width: 767px)');
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const avatarInitial = user?.name?.charAt(0).toUpperCase() ?? '?';

  const isActive = (to: string) => {
    if (to === '/dashboard' || to === '/home') return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  return (
    <div className="flex h-screen bg-cream-100 overflow-hidden">

      {/* ── Sidebar (desktop only) ──────────────────────────────────── */}
      <aside className="hidden md:flex flex-col w-[220px] bg-[#141e28] shrink-0">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-white/10">
          <KinCareLogo variant="dark" />
        </div>

        {/* Workspace switcher (only when user has multiple workspaces) */}
        <div className="px-3 pt-3">
          <WorkspaceSwitcher />
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {SIDEBAR_NAV_ITEMS.map(({ to, label, Icon }) => {
            const active = isActive(to);
            return (
              <NavLink
                key={to}
                to={to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? 'bg-white/10 text-white'
                    : 'text-[#a8bcc8] hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span>{label}</span>
                {to === '/messages' && totalUnreadCount > 0 && (
                  <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                  </span>
                )}
                {to === '/notifications' && notificationCount > 0 && (
                  <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-white">
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom: settings + avatar */}
        <div className="px-3 py-4 border-t border-white/10 space-y-0.5">
          <button
            onClick={() => navigate('/settings')}
            className={`flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              location.pathname.startsWith('/settings')
                ? 'bg-white/10 text-white font-medium'
                : 'text-[#a8bcc8] hover:bg-white/[0.06] hover:text-white'
            }`}
          >
            <GearIcon className="w-5 h-5 shrink-0" />
            <span>Settings</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className={`flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              location.pathname === '/profile'
                ? 'bg-white/10 text-white font-medium'
                : 'text-[#a8bcc8] hover:bg-white/[0.06] hover:text-white'
            }`}
          >
            <div className="w-5 h-5 rounded-full bg-white/10 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
              {avatarInitial}
            </div>
            <span className="truncate">{user?.name ?? 'Profile'}</span>
          </button>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* ── Mobile top bar ─────────────────────────────────────── */}
        <header className="md:hidden bg-white border-b border-gray-200 flex items-center justify-between px-4 h-12 shrink-0 z-10">
          <KinCareLogo variant="light" />
          <div className="flex items-center gap-2">
            {/* Bell → notifications page */}
            <button
              onClick={() => navigate('/notifications')}
              className="relative p-1.5 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              aria-label={notificationCount > 0 ? `${notificationCount} pending approvals` : 'Notifications'}
            >
              <BellIcon className="w-5 h-5" />
              {notificationCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent-500 text-[10px] font-bold text-white">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 text-sm font-bold flex items-center justify-center"
              aria-label="Profile"
            >
              {avatarInitial}
            </button>
          </div>
        </header>

        {/* ── Page content ───────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>

        {/* ── Mobile bottom tab bar ──────────────────────────────── */}
        <nav className="md:hidden bg-white border-t border-gray-200 flex shrink-0 safe-area-inset-bottom z-10">
          {MOBILE_TAB_ITEMS.map(({ to, label, Icon }) => {
            const active = isActive(to);
            return (
              <NavLink
                key={to}
                to={to}
                className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition-colors relative ${
                  active ? 'text-primary-500' : 'text-[#a8bcc8] hover:text-primary-400'
                }`}
              >
                <div className="relative">
                  <Icon className="w-6 h-6" />
                  {to === '/messages' && totalUnreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-0.5 text-[8px] font-bold text-white">
                      {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
                    </span>
                  )}
                </div>
                <span>{label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
